/**
 * Typed fetch client for the AOS worker REST API.
 *
 * Single source of HTTP behaviour for the portal: base URL resolution,
 * `credentials: include` (so the worker session cookie rides along), JSON
 * encoding, and error normalization into a thrown {@link ApiError}. The Refine
 * data/auth/access providers (and the custom dashboard fetches) all go through
 * this client — see IMPLEMENTATION_CONTRACT §5 for the wire conventions.
 */

/**
 * Base URL for the worker API. From `import.meta.env.VITE_API_URL`, defaulting
 * to the same-origin `/api` prefix (in dev, Vite proxies `/api` → the worker).
 * Trailing slashes are trimmed so callers can pass paths that start with `/`.
 */
export const API_URL: string = (
  (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'
).replace(/\/+$/, '');

/** Shape of the worker error body (IMPLEMENTATION_CONTRACT §5). */
export interface ApiErrorBody {
  error: string;
  details?: unknown;
}

/**
 * Normalized error thrown by every {@link api} method on a non-2xx response (or
 * a network/parse failure). `status` mirrors the HTTP status (0 for transport
 * failures) so the auth provider can branch on `401`. `code` is the worker's
 * machine-readable `error` string when available.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Options accepted by the low-level {@link request} helper. */
export interface RequestOptions {
  /** Query parameters appended to the URL (skipped when `undefined`/`null`). */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Parsed and sent as a JSON body with the appropriate content-type. */
  body?: unknown;
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>;
  /** Abort signal forwarded to `fetch`. */
  signal?: AbortSignal;
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/** Build a fully-qualified URL from a path + optional query params. */
function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('/') ? `${API_URL}${path}` : `${API_URL}/${path}`;
  if (!query) return base;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Parse a response body as JSON when the content-type says so; otherwise return
 * the raw text (or `undefined` for an empty body). Never throws on its own.
 */
async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return undefined;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

/**
 * Core request primitive. Resolves with the parsed JSON payload typed as `T` on
 * a 2xx response; throws {@link ApiError} otherwise (including transport and
 * parse failures, surfaced as `status: 0`).
 */
async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    accept: 'application/json',
    ...options.headers,
  };

  const init: RequestInit = {
    method,
    headers,
    credentials: 'include',
    signal: options.signal,
  };

  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'Network request failed';
    throw new ApiError(0, 'network_error', message, cause);
  }

  const payload = await parseBody(response);

  if (!response.ok) {
    const errorBody =
      payload && typeof payload === 'object' ? (payload as Partial<ApiErrorBody>) : undefined;
    const code = errorBody?.error ?? 'internal_error';
    const message = `${method} ${path} failed with ${response.status}: ${code}`;
    throw new ApiError(response.status, code, message, errorBody?.details);
  }

  return payload as T;
}

/**
 * The typed API client. Methods mirror the verbs used by the worker. Each
 * resolves with the parsed JSON payload and rejects with {@link ApiError}.
 */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>): Promise<T> =>
    request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, options),
};

export type Api = typeof api;
